import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight, Sparkles, BookOpen, MessageSquare, Share2, Bookmark, BookmarkCheck, Users, Rss, Library, X } from 'lucide-react';
import { toast } from 'sonner';
import { shareViaNative } from '@/utils/nativeShare';
import ScripturePopup from '@/components/ScripturePopup';
import ShareToFriendModal from '@/components/ShareToFriendModal';
import PostToFeedModal from '@/components/PostToFeedModal';
import ShareDYKModal from '@/components/ShareDYKModal';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';
import { useSignInGate } from '@/hooks/useSignInGate';

import { useTranslation } from 'react-i18next';

/**
 * DidYouKnowPage — full reader for the Did You Know feature.
 *
 * State machine:
 *   • feed[]      — published facts, newest first (current + history)
 *   • index       — which fact in the feed is visible
 *   • savedSet    — fact_ids the user has saved
 *
 * On mount we POST `mark-read` for whichever fact opens first so the
 * NEW badge on the home card clears.
 *
 * Buttons:
 *   • Discuss with Eili → POST a new conversation seeded with the fact.
 *   • Share → uses the existing nativeShare helper.
 *   • Save → toggles `did_you_know_saves`.
 */
export default function DidYouKnowPage({ user }) {
  const {
    t,
    i18n
  } = useTranslation();

  const navigate = useNavigate();
  const { requireSignIn, signInModal } = useSignInGate(user);
  const [feed, setFeed] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  // Feature-flag gates.
  const { enabled: dykShareEnabled } = useFeatureFlag('dyk_share');
  const { enabled: communityShareEnabled } = useFeatureFlag('community_share');

  const apiBase = `${window.location.origin}/api`;
  const lang = (i18n.language || 'en').split('-')[0];

  const fact = feed[index];

  const markRead = useCallback(async (factId) => {
    if (!factId) return;
    try {
      await axios.post(`${apiBase}/did-you-know/${factId}/mark-read`, {}, {
        withCredentials: true,
        timeout: 4000,
      });
    } catch {
      // ignore
    }
  }, [apiBase]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${apiBase}/did-you-know/history?limit=20`, {
          params: { locale: lang },
          withCredentials: true,
          timeout: 6000,
        });
        if (cancelled) return;
        const facts = res.data?.facts || [];
        setFeed(facts);
        setHasMore(res.data?.has_more || false);
        if (facts[0]) markRead(facts[0].fact_id);
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, markRead, lang]);

  // Mark each fact read as the user swipes to it.
  useEffect(() => {
    if (fact && !fact.is_read) markRead(fact.fact_id);
  }, [fact, markRead]);

  const goPrev = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = async () => {
    if (index < feed.length - 1) {
      setIndex(index + 1);
      return;
    }
    if (!hasMore) return;
    // Lazy-load older facts.
    const last = feed[feed.length - 1];
    try {
      const res = await axios.get(
        `${apiBase}/did-you-know/history?limit=20&before=${encodeURIComponent(last.published_at)}`,
        { params: { locale: lang }, withCredentials: true, timeout: 6000 }
      );
      const more = res.data?.facts || [];
      if (more.length) {
        setFeed((prev) => [...prev, ...more]);
        setHasMore(res.data?.has_more || false);
        setIndex(index + 1);
      } else {
        setHasMore(false);
      }
    } catch {
      // ignore
    }
  };

  const handleSave = async () => {
    if (!fact) return;
    if (requireSignIn('save this fact')) return;
    try {
      const res = await axios.post(
        `${apiBase}/did-you-know/${fact.fact_id}/save`,
        {},
        { withCredentials: true, timeout: 4000 }
      );
      const saved = !!res.data?.saved;
      setFeed((prev) => prev.map((f, i) =>
        i === index ? { ...f, is_saved: saved } : f
      ));
      // Keep savedFacts panel in sync so re-opening reflects new state.
      setSavedFacts((prev) => {
        const fid = fact.fact_id;
        if (saved) {
          if (prev.some((p) => p.fact_id === fid)) return prev;
          return [{ ...fact, is_saved: true }, ...prev];
        }
        return prev.filter((p) => p.fact_id !== fid);
      });
      toast.success(saved ? 'Saved to your library' : 'Removed from library');
    } catch {
      toast.error(t('generated.didyouknowpage_could_not_save_7cef'));
    }
  };

  // Lazy-load the saved-facts library when the panel opens.
  const openSavedPanel = async () => {
    setShowSavedPanel(true);
    setLoadingSaved(true);
    try {
      const res = await axios.get(`${apiBase}/did-you-know/saved?limit=100`, {
        params: { locale: lang },
        withCredentials: true, timeout: 6000,
      });
      setSavedFacts(res.data?.facts || []);
    } catch {
      toast.error(t('generated.didyouknowpage_could_not_load_5252'));
    } finally {
      setLoadingSaved(false);
    }
  };

  // Tap a saved fact → jump to it in the main viewer.
  const openSavedFact = (saved) => {
    const existingIdx = feed.findIndex((f) => f.fact_id === saved.fact_id);
    if (existingIdx >= 0) {
      setIndex(existingIdx);
    } else {
      setFeed((prev) => [saved, ...prev]);
      setIndex(0);
    }
    setShowSavedPanel(false);
  };

  const handleDiscuss = async () => {
    if (!fact) return;
    try {
      const res = await axios.get(
        `${apiBase}/did-you-know/discuss-seed/${fact.fact_id}`,
        { params: { locale: lang }, withCredentials: true, timeout: 4000 }
      );
      const seed = res.data?.seed_message || `Topic: ${fact.title}\n\n`;
      const suggestedQuestions = res.data?.suggested_questions || [];
      navigate('/chat', {
        state: {
          seedMessage: seed,
          suggestedQuestions,
          source: 'did_you_know',
          factId: fact.fact_id,
        },
      });
    } catch {
      navigate('/chat', {
        state: {
          seedMessage: `Topic: ${fact.title}\n\n`,
          suggestedQuestions: [],
          source: 'did_you_know',
        },
      });
    }
  };

  const handleShare = async () => {
    if (!fact) return;
    let url = `${window.location.origin}/did-you-know?fact=${fact.fact_id}`;
    let text = fact.title;
    // Mint a proper public share record so the link unfurls with a
    // content-specific OG card (badge + topic + body excerpt) on
    // iMessage / WhatsApp / Slack / Twitter / etc. Falls back to the
    // plain in-app URL if the share endpoint is unavailable.
    try {
      const res = await axios.post(
        `${apiBase}/did-you-know/share`,
        { fact_id: fact.fact_id },
        { withCredentials: true, timeout: 6000 },
      );
      if (res.data?.share_id) {
        url = `${window.location.origin}/api/og/share/${res.data.share_id}`;
      }
    } catch {
      // Non-fatal — keep the in-app URL fallback.
    }
    try {
      const result = await shareViaNative({
        title: t('generated.didyouknowpage_did_you_know_c324'),
        text,
        url,
      });
      if (result?.aborted) return;
      if (result?.shared) return;
    } catch {
      // fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success(t('generated.didyouknowpage_link_copied_to_215f'));
    } catch {
      toast.error(t('generated.didyouknowpage_could_not_copy_42bd'));
    }
  };

  // ── Scripture popup state ───────────────────────────────────────────
  // Tapping a Bible reference chip now shows an inline popup with the
  // verse text + "Read Full Chapter" CTA — mirrors the reflection page
  // behaviour so users don't get yanked away from the current fact.
  const [scripturePopup, setScripturePopup] = useState(null);

  // ── Share-to-Friend / Post-to-Feed modals (mirrors reflections page) ─
  // We reuse the same modal contracts the reflection share row uses;
  // they accept a `message` shape with content + optional _question
  // and a `conversationTitle`. Image-share path inside ShareToFriend
  // gracefully degrades when message_id is absent (DYK facts have no
  // message_share doc yet) — text+link share still works.
  const [shareToFriendData, setShareToFriendData] = useState(null);
  const [postToFeedData, setPostToFeedData] = useState(null);
  // Themed share-card modal — parity with ShareReflectionModal / ShareModal.
  // When non-null, opens the full DYK share UI (theme picker + preview +
  // Share-Image / Save / Link / Friend buttons). The bare `handleShare`
  // shortcut below remains for legacy callers but the primary entry
  // point is now this modal.
  const [shareModalFact, setShareModalFact] = useState(null);
  // "Saved" library panel — user-requested dedicated section to find
  // facts they've saved. Lazy-loads via /api/did-you-know/saved when
  // opened; tapping a saved fact prepends it into the feed at index 0
  // so it renders in the same viewer the rest of the page uses.
  const [showSavedPanel, setShowSavedPanel] = useState(false);
  const [savedFacts, setSavedFacts] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);

  const factToMessage = (f) => ({
    content: f.content || f.title,
    _question: f.title,
    // Intentionally no `message_id` — keeps image-share path disabled
    // until we wire a DYK-specific share-card endpoint later.
  });
  const handleShareToFriend = () => {
    if (!fact) return;
    setShareToFriendData({
      message: factToMessage(fact),
      title: `Did You Know? ${fact.title}`,
    });
  };
  const handlePostToFeed = () => {
    if (!fact) return;
    setPostToFeedData({
      message: factToMessage(fact),
      title: `Did You Know? ${fact.title}`,
    });
  };

  const openBibleRef = (ref) => {
    setScripturePopup(ref);
  };

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center" style={{ background: 'var(--theme-background)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--theme-accent)' }} />
      </div>
    );
  }

  if (!fact) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center p-6" style={{ background: 'var(--theme-background)' }}>
        <Sparkles className="h-12 w-12 mb-4" style={{ color: 'var(--theme-accent)' }} />
        <p style={{ color: 'var(--theme-text)' }}>{t('generated.didyouknowpage_no_facts_available_372d')}</p>
        <button
          onClick={() => navigate('/home')}
          className="mt-4 px-4 py-2 rounded-xl"
          style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
          data-testid="dyk-back-home"
        >
          {t('generated.didyouknowpage_back_to_home_7b4a')}
        </button>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ background: 'var(--theme-background)' }} data-testid="did-you-know-page">
      {/* Header */}
      <header
        className="flex items-center gap-3 px-4 py-3 border-b shrink-0"
        style={{
          background: 'var(--theme-background)',
          borderColor: 'var(--glass-border)',
          paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)',
        }}
      >
        <button
          onClick={() => navigate('/home')}
          className="p-2 rounded-lg"
          style={{ color: 'var(--theme-text)' }}
          data-testid="dyk-page-back-btn"
          aria-label={t('generated.didyouknowpage_back_to_home_7b4a')}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2 flex-1">
          <Sparkles className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
          <h1 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>
            {t('generated.didyouknowpage_did_you_know_c324')}
          </h1>
        </div>
        <button
          onClick={openSavedPanel}
          className="p-2 rounded-lg flex items-center gap-1.5 text-sm transition-colors hover:bg-white/5"
          style={{ color: 'var(--theme-text)' }}
          data-testid="dyk-saved-library-btn"
          aria-label={t('generated.didyouknowpage_open_saved_did_0965')}
          title={t('generated.didyouknowpage_saved_facts_1716')}
        >
          <Library className="h-4 w-4" />
          <span className="hidden sm:inline">{t('generated.didyouknowpage_saved_fd40')}</span>
        </button>
      </header>
      {/* Fact content — scrollable region */}
      <main className="page-content" data-testid="dyk-fact-body">
        <div className="max-w-2xl mx-auto px-5 py-6 pb-32">
        <div className="mb-2 flex items-center gap-2">
          <span
            className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium"
            style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
            data-testid="dyk-fact-category"
          >
            {fact.category?.replace(/_/g, ' ')}
          </span>
        </div>
        <h2
          className="text-2xl sm:text-3xl font-bold mb-4"
          style={{ color: 'var(--theme-text)' }}
          data-testid="dyk-fact-title"
        >
          {fact.title}
        </h2>
        <div
          className="prose prose-invert text-base leading-relaxed mb-6 whitespace-pre-wrap"
          style={{ color: 'var(--theme-text)' }}
          data-testid="dyk-fact-content"
        >
          {fact.content}
        </div>

        {/* Scripture refs as tappable chips */}
        {fact.scripture_refs?.length > 0 && (
          <div className="mb-6">
            <p className="text-xs uppercase tracking-wider mb-2 font-medium" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.didyouknowpage_related_scripture_aa03')}
            </p>
            <div className="flex flex-wrap gap-2" data-testid="dyk-scripture-chips">
              {fact.scripture_refs.map((ref) => (
                <button
                  key={ref}
                  onClick={() => openBibleRef(ref)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all hover:scale-105 active:scale-95"
                  style={{
                    background: 'var(--glass-bg-active)',
                    color: 'var(--theme-accent)',
                    border: '1px solid var(--glass-border)',
                  }}
                  data-testid={`dyk-scripture-chip-${ref.replace(/\s+/g, '-')}`}
                >
                  <BookOpen className="h-3.5 w-3.5" />
                  {ref}
                </button>
              ))}
            </div>
          </div>
        )}

        {fact.historical_refs?.length > 0 && (
          <div className="mb-6">
            <p className="text-xs uppercase tracking-wider mb-2 font-medium" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.didyouknowpage_historical_sources_aee0')}
            </p>
            <ul className="text-sm space-y-1" style={{ color: 'var(--theme-textSecondary)' }} data-testid="dyk-historical-sources">
              {fact.historical_refs.map((s) => (<li key={s}>• <em>{s}</em></li>))}
            </ul>
          </div>
        )}

        {/* Action row */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={handleDiscuss}
            className="flex-1 min-w-[160px] rounded-xl px-5 py-3 font-medium flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: 'var(--theme-accent)', color: 'white' }}
            data-testid="dyk-discuss-btn"
          >
            <MessageSquare className="h-4 w-4" />
            {t('generated.didyouknowpage_discuss_with_eili_c158')}
          </button>
          {dykShareEnabled && (
          <button
            onClick={() => setShareModalFact(fact)}
            className="rounded-xl px-4 py-3 font-medium flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-text)' }}
            data-testid="dyk-share-btn"
          >
            <Share2 className="h-4 w-4" />
            {t('generated.didyouknowpage_share_92d8')}
          </button>
          )}
          {communityShareEnabled && (
          <button
            onClick={handleShareToFriend}
            className="rounded-xl px-4 py-3 font-medium flex items-center justify-center gap-2 transition-all hover:bg-[var(--theme-hover)]"
            style={{ color: 'var(--theme-text)' }}
            data-testid="dyk-share-friend-btn"
          >
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">{t('generated.didyouknowpage_share_with_friend_3990')}</span>
            <span className="sm:hidden">{t('generated.didyouknowpage_friend_d7d0')}</span>
          </button>
          )}
          {communityShareEnabled && (
          <button
            onClick={handlePostToFeed}
            className="rounded-xl px-4 py-3 font-medium flex items-center justify-center gap-2 transition-all hover:bg-[var(--theme-hover)]"
            style={{ color: 'var(--theme-text)' }}
            data-testid="dyk-post-feed-btn"
          >
            <Rss className="h-4 w-4" />
            <span className="hidden sm:inline">{t('generated.didyouknowpage_post_to_feed_46bf')}</span>
            <span className="sm:hidden">{t('generated.didyouknowpage_feed_ccc4')}</span>
          </button>
          )}
          <button
            onClick={handleSave}
            className="rounded-xl px-4 py-3 font-medium flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: 'var(--glass-bg-active)', color: fact.is_saved ? 'var(--theme-accent)' : 'var(--theme-text)' }}
            data-testid="dyk-save-btn"
          >
            {fact.is_saved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
            {fact.is_saved ? 'Saved' : 'Save'}
          </button>
        </div>

        {/* Nav: prev/next through history */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: 'var(--glass-border)' }}>
          <button
            onClick={goPrev}
            disabled={index === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg disabled:opacity-30"
            style={{ color: 'var(--theme-text)' }}
            data-testid="dyk-prev-btn"
          >
            <ChevronLeft className="h-4 w-4" />
            {t('generated.didyouknowpage_newer_1cf0')}
          </button>
          <span className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
            {index + 1} of {feed.length}{hasMore ? '+' : ''}
          </span>
          <button
            onClick={goNext}
            disabled={index >= feed.length - 1 && !hasMore}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg disabled:opacity-30"
            style={{ color: 'var(--theme-text)' }}
            data-testid="dyk-next-btn"
          >
            {t('generated.didyouknowpage_older_acd3')}
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        </div>
      </main>
      {/* ── Scripture reference popup ───────────────────────────────── */}
      {scripturePopup && (
        <ScripturePopup
          reference={scripturePopup}
          onClose={() => setScripturePopup(null)}
        />
      )}
      {/* ── Share with Friend modal (reused from reflections) ────────── */}
      {shareToFriendData && (
        <ShareToFriendModal
          message={shareToFriendData.message}
          conversationTitle={shareToFriendData.title}
          onClose={() => setShareToFriendData(null)}
        />
      )}
      {/* ── Post to Feed modal (reused from reflections) ────────────── */}
      {postToFeedData && (
        <PostToFeedModal
          message={postToFeedData.message}
          conversationTitle={postToFeedData.title}
          onClose={() => setPostToFeedData(null)}
        />
      )}
      {/* ── DYK themed share-card modal (parity with reflection share) ─ */}
      {shareModalFact && (
        <ShareDYKModal
          fact={shareModalFact}
          onClose={() => setShareModalFact(null)}
          onShareToFriend={communityShareEnabled ? () => handleShareToFriend() : null}
        />
      )}
      {/* ── Saved Library panel (slide-down) ─────────────────────────
          User-requested dedicated section to locate saved facts.
          Lazy-fetches /api/did-you-know/saved on open; tapping a saved
          fact jumps the main viewer to it (prepends into feed if not
          already present). Uses --visual-vh so it never clips behind
          the iOS keyboard.
      */}
      {showSavedPanel && (
        <div
          className="fixed inset-0 z-50 flex flex-col"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowSavedPanel(false)}
          data-testid="dyk-saved-panel"
        >
          <div
            className="mt-auto w-full max-w-2xl mx-auto rounded-t-2xl overflow-hidden flex flex-col"
            style={{
              background: 'var(--theme-surface, #1e293b)',
              border: '1px solid var(--glass-border, #334155)',
              maxHeight: 'min(85vh, calc(var(--visual-vh, 100vh) - 32px))',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 py-4 border-b"
                 style={{ borderColor: 'var(--glass-border, #334155)' }}>
              <div className="flex items-center gap-2">
                <Library className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
                <h2 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>
                  {t('generated.didyouknowpage_your_saved_facts_d17a')}
                </h2>
                {!loadingSaved && (
                  <span className="text-xs opacity-60 ml-1" style={{ color: 'var(--theme-textSecondary)' }}>
                    {savedFacts.length}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowSavedPanel(false)}
                className="p-1.5 rounded-full transition hover:bg-white/10"
                data-testid="dyk-saved-panel-close"
                aria-label={t('generated.didyouknowpage_close_saved_facts_b577')}
              >
                <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="dyk-saved-list">
              {loadingSaved ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                       style={{ borderColor: 'var(--theme-accent)', borderTopColor: 'transparent' }} />
                </div>
              ) : savedFacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-6"
                     data-testid="dyk-saved-empty">
                  <Bookmark className="h-10 w-10 mb-3 opacity-30" style={{ color: 'var(--theme-text)' }} />
                  <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>
                    {t('generated.didyouknowpage_no_saved_facts_76f9')}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                    {t('generated.didyouknowpage_tap_the_bookmark_970d')}
                  </p>
                </div>
              ) : (
                <ul className="divide-y" style={{ borderColor: 'var(--glass-border)' }}>
                  {savedFacts.map((sf) => (
                    <li key={sf.fact_id}>
                      <button
                        onClick={() => openSavedFact(sf)}
                        className="w-full text-left px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
                        data-testid={`dyk-saved-item-${sf.fact_id}`}
                      >
                        <div className="flex items-start gap-3">
                          <BookmarkCheck className="h-4 w-4 shrink-0 mt-1"
                                         style={{ color: 'var(--theme-accent)' }} />
                          <div className="flex-1 min-w-0">
                            {sf.category && (
                              <span className="text-[10px] uppercase tracking-wider"
                                    style={{ color: 'var(--theme-textSecondary)' }}>
                                {sf.category.replace(/_/g, ' ')}
                              </span>
                            )}
                            <p className="text-sm font-semibold mb-1"
                               style={{ color: 'var(--theme-text)' }}>
                              {sf.title}
                            </p>
                            <p className="text-xs line-clamp-2 leading-relaxed"
                               style={{ color: 'var(--theme-textSecondary)' }}>
                              {sf.content}
                            </p>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      {signInModal}
    </div>
  );
}
